import { IH2OPalette, IH2OSemanticColors } from '../models/components.models';

export const cssVar = (name: string, fallback = '') => `var(--h2o-${name}, ${fallback})`;
export const defaultPalette: IH2OPalette = {
  // from Style Guide + UI Design, All the colors in UI should be here

  primary100: cssVar('primary100', '#FFF9E6'),
  primary200: cssVar('primary200', '#FFECB3'),
  primary300: cssVar('primary300', '#FFE28C'),
  primary400: cssVar('primary400', '#FFD659'),
  primary500: cssVar('primary500', '#FEC925'),
  primary600: cssVar('primary600', '#E6AC00'),
  primary700: cssVar('primary700', '#BF8F00'),
  primary800: cssVar('primary800', '#A67C00'),
  primary900: cssVar('primary900', '#664D00'),

  // grays
  white: cssVar('white', '#FFF'),
  gray200: cssVar('gray200', '#F3F4F6'),
  gray300: cssVar('gray300', '#D1D5DB'),
  gray350: cssVar('gray350', '#CCCCCC'),
  gray400: cssVar('gray400', '#9CA3AF'),
  gray500: cssVar('gray500', '#6B7280'),
  gray600: cssVar('gray600', '#676973'),
  gray700: cssVar('gray700', '#414248'),
  gray750: cssVar('gray750', '#323232'),
  gray800: cssVar('gray800', '#1C1D21'),
  gray900: cssVar('gray900', '#080808'),
  black: cssVar('black', '#000000'),

  // transparent grays. naming convention: colorName + _a_ + alpha value. e.g gray900_a_60 would be gray900 at 0.60 alpha;
  gray400_a_16: cssVar('gray400_a_16', '#1C1D2129'),
  gray900_a_60: cssVar('gray900_a_60', '#08080898'),
  black_a_4: cssVar('black_a_4', '#0000000B'),
  black_a_8: cssVar('black_a_8', '#00000014'),
  gray300_a_50: cssVar('gray300_a_50', '#D1D5DB80'),
  gray300_a_16: cssVar('gray300_a_16', '#D1D5DB29'),

  // brand yellow
  yellow100: cssVar('yellow100', '#FFF9E6'),
  yellow200: cssVar('yellow200', '#FFECB3'),
  yellow300: cssVar('yellow300', '#FFE28C'),
  yellow400: cssVar('yellow400', '#FFD659'),
  yellow500: cssVar('yellow500', '#FEC925'), // <-- H2O brand color
  yellow600: cssVar('yellow600', '#E6AC00'),
  yellow700: cssVar('yellow700', '#BF8F00'),
  yellow800: cssVar('yellow800', '#A67C00'),
  yellow900: cssVar('yellow900', '#664D00'),

  // danger red
  red100: cssVar('red100', '#FFD9D9'),
  red200: cssVar('red200', '#FF8080'),
  red300: cssVar('red300', '#ED5F5F'),
  red400: cssVar('red400', '#D43F3F'),
  red500: cssVar('red500', '#BA2525'),
  red600: cssVar('red600', '#991D1D'),
  red700: cssVar('red700', '#801818'),
  red800: cssVar('red800', '#661313'),
  red900: cssVar('red900', '#4D0F0F'),

  // success green
  green100: cssVar('green100', '#D0F5D1'),
  green200: cssVar('green200', '#A4DBA6'),
  green300: cssVar('green300', '#7EC280'),
  green400: cssVar('green400', '#5DA85F'),
  green500: cssVar('green500', '#3F9142'),
  green600: cssVar('green600', '#347836'),
  green700: cssVar('green700', '#295E2A'),
  green800: cssVar('green800', '#1E451F'),
  green900: cssVar('green900', '#132B13'),

  // info blue
  blue100: cssVar('blue100', '#E6F2FF'),
  blue200: cssVar('blue200', '#A6CFFF'),
  blue300: cssVar('blue300', '#73B4FF'),
  blue400: cssVar('blue400', '#3D93F5'),
  blue500: cssVar('blue500', '#0967D2'),
  blue600: cssVar('blue600', '#085DBF'),
  blue700: cssVar('blue700', '#06448C'),
  blue800: cssVar('blue800', '#053873'),
  blue900: cssVar('blue900', '#042C59'),

  transparent: cssVar('transparent', 'transparent'),
  textOverPrimary: cssVar('textOverPrimary', '#080808'), // Default gray900
};

export const defaultSemanticColors: Partial<IH2OSemanticColors> | { [key: string]: string } = {
  //// Base slots
  bodyBackground: defaultPalette.gray200, // body and page background
  contentBackground: defaultPalette.white,
  bodyFrameBackground: defaultPalette.gray200,
  textPrimary: defaultPalette.gray900, // gray900 was Colors..textScondary.
  textOverPrimary: defaultPalette.textOverPrimary, // Either black or white depending on the primary color to ensure enough contrast.
  // gray500 (#999) only reaches 2.85:1 on white and 2.71:1 on the page background — below the
  // WCAG AA 4.5:1 floor for body text. gray600 clears it (5.74:1 / 5.45:1) and matches the current
  // design, where `text-muted` is also gray600. See colors.spec.ts.
  textSecondary: defaultPalette.gray600,
  textQuaternary: defaultPalette.gray800, // gray800 was Colors..textPrimary
  textQuinary: defaultPalette.gray600,
  textDisabled: defaultPalette.gray400,

  //// Invariants - slots that rarely change color theme-to-theme because the color has meaning

  //// Components
  // Link
  linkText: defaultPalette.blue500,
  linkOverPrimary: defaultPalette.textOverPrimary,
  // Footer
  footerBackground: defaultPalette.primary500,
  // Button
  // default button - aka secondary button
  buttonText: defaultPalette.gray600,
  buttonDefaultSecondaryText: defaultPalette.gray600,
  buttonBorder: defaultPalette.gray500,
  buttonHoverBackground: defaultPalette.gray300,
  buttonHoverText: defaultPalette.gray600,
  buttonHoverBorder: defaultPalette.gray500,
  buttonActiveBorder: defaultPalette.gray500,
  buttonActiveBackground: defaultPalette.gray200,
  buttonDisabledBackground: defaultPalette.gray200,
  buttonDisabledBorderColor: defaultPalette.gray400,

  buttonPrimaryText: defaultPalette.textOverPrimary,
  buttonPrimaryBackground: defaultPalette.primary500,
  buttonPrimaryHoverBackground: defaultPalette.primary400,
  buttonPrimaryActiveBackground: defaultPalette.primary400,
  buttonPrimaryActiveBorder: defaultPalette.primary900,
  buttonPrimaryDisabledBackground: defaultPalette.gray300,
  buttonPrimaryDisabledBorder: defaultPalette.gray300,
  buttonPrimaryDisabledText: defaultPalette.gray500,

  buttonSecondaryText: defaultPalette.white,
  buttonSecondaryBackground: defaultPalette.gray700,
  buttonSecondaryHoverBackground: defaultPalette.gray900,
  buttonSecondaryActiveBackground: defaultPalette.gray600,
  buttonSecondaryActiveBorder: defaultPalette.gray800,
  buttonSecondaryDisabledBackground: defaultPalette.gray300,
  buttonSecondaryDisabledBorder: defaultPalette.gray400,
  buttonSecondaryDisabledText: defaultPalette.gray500,

  buttonGhostText: defaultPalette.gray750,
  buttonGhostBackground: defaultPalette.white,
  buttonGhostBorder: defaultPalette.gray750,
  buttonGhostBackgroundHovered: defaultPalette.white,
  buttonGhostBorderHovered: defaultPalette.black,
  buttonGhostTextHovered: defaultPalette.black,
  buttonGhostBackgroundFocused: defaultPalette.gray200,
  buttonGhostTextFocused: defaultPalette.gray750,
  buttonGhostBorderFocused: defaultPalette.gray750,
  buttonGhostBackgroundActive: defaultPalette.gray400,
  buttonGhostBorderActive: defaultPalette.gray900,
  buttonGhostTextActive: defaultPalette.gray900,
  buttonGhostBackgroundDisabled: defaultPalette.gray200,
  buttonGhostTextDisabled: defaultPalette.gray350,
  buttonGhostBorderDisabled: defaultPalette.gray350,

  buttonDanger: defaultPalette.red500,
  buttonDangerHovered: defaultPalette.red600,
  buttonDangerActiveBorder: defaultPalette.red900,
  buttonDangerDisabled: defaultPalette.red100,

  buttonStealthText: defaultPalette.gray900,
  buttonStealthBackground: defaultPalette.transparent,
  buttonStealthHoverBackground: defaultPalette.gray300,
  buttonStealthActiveBackground: defaultPalette.gray300,
  buttonStealthActiveBorder: defaultPalette.gray500,
  buttonStealthDisabledBackground: defaultPalette.transparent,

  buttonIconColor: defaultPalette.gray500,
  buttonDividerBackground: defaultPalette.white,
  buttonIconColumn: defaultPalette.primary800,
  buttonIconColumnHovered: defaultPalette.primary600,
  buttonLinkText: defaultPalette.primary800,
  buttonLinkIcon: defaultPalette.primary900,
  buttonLinkHoverText: defaultPalette.primary900,
  buttonLinkUnderlineText: defaultPalette.blue500,
  buttonLinkUnderlineHoverText: defaultPalette.blue700,
  buttonCarouselIcon: defaultPalette.primary400,

  buttonInlineBackground: defaultPalette.gray300,
  buttonInlineBorder: defaultPalette.gray300,
  buttonInlineHoverBackground: defaultPalette.gray200,
  buttonInlineHoverText: defaultPalette.gray900,
  buttonInlineSuccess: defaultPalette.green500,

  buttonCarouselBackground: defaultPalette.gray900,
  buttonCarouselBackgroundHovered: defaultPalette.gray700,

  // Form Fields
  inputBorder: defaultPalette.gray300,
  inputText: defaultPalette.gray700,
  inputReadOnlyText: defaultPalette.gray900,
  inputLabel: defaultPalette.gray800,
  inputPlaceholder: defaultPalette.gray600,
  inputActiveBorder: defaultPalette.blue500,
  inputErrorBorder: defaultPalette.red500,
  inputErrorMessageText: defaultPalette.red500,
  inputSuccessBorder: defaultPalette.green500,
  inputSuccessMessageText: defaultPalette.green500,
  inputDisabledBackground: defaultPalette.gray200,
  inputDisabledNoBorder: defaultPalette.gray200,
  inputDisabledBorder: defaultPalette.gray300,
  inputListItemHoveredBackground: defaultPalette.gray200,
  inputListItemActiveBackground: defaultPalette.primary400,

  dropdownInlineBorder: defaultPalette.gray400,

  fileUploadActiveBorder: defaultPalette.primary500,
  checkboxCheckMark: defaultPalette.textOverPrimary,
  checkboxCheckMarkErr: defaultPalette.white,

  // Message bar
  messageBarTitleText: defaultPalette.gray900,
  messageBarContentText: defaultPalette.gray900,
  messageBarContentTextSuccess: defaultPalette.green900,
  messageBarContentTextInfo: defaultPalette.blue900,
  messageBarContentTextError: defaultPalette.red900,
  messageBarContentTextWarning: defaultPalette.yellow900,
  messageBarIconSuccess: defaultPalette.green500,
  messageBarBackgroundSuccess: defaultPalette.green100,
  messageBarIconInfo: defaultPalette.blue500,
  messageBarBackgroundInfo: defaultPalette.blue100,
  messageBarIconError: defaultPalette.red500,
  messageBarBackgroundError: defaultPalette.red100,
  messageBarIconWarning: defaultPalette.yellow500,
  messageBarBackgroundWarning: defaultPalette.yellow100,
  // loader
  loaderBarIndicator: defaultPalette.green500,
  loaderBarIndicatorButton: defaultPalette.primary700,
  loaderBarIndicatorError: defaultPalette.red500,
  loaderBarBackground: defaultPalette.gray200,
  loaderIndicator: defaultPalette.primary700,
  loader: defaultPalette.primary200,
  loaderBarLabel: defaultPalette.gray600,
  loaderBarDescription: defaultPalette.gray900,
  loaderLabel: defaultPalette.primary800,

  // tag & category
  categoryBackground: defaultPalette.blue100,
  categoryText: defaultPalette.blue900,
  tagBackground: defaultPalette.gray200,
  tagText: defaultPalette.gray700,
  tagBorderLeft: defaultPalette.primary500,
  // tooltip
  tooltipBackground: defaultPalette.gray700,
  tooltipText: defaultPalette.gray300,
  // nav
  navText: defaultPalette.gray800,
  navSelectedBackground: defaultPalette.primary500,
  // etc
  liked: defaultPalette.red400,
};
